import { useEffect, useState } from "react";
import { api, friendlyError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Eye, Clock, FileText } from "lucide-react";

const STATUS_LABEL = {
  draft: "Draft",
  sent: "Awaiting your quotation",
  quote_received: "Quotation submitted",
  closed: "Closed",
};

function timeAgo(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function SupplierRFQs() {
  const [rfqs, setRfqs] = useState([]);
  const [open, setOpen] = useState(null);
  const [form, setForm] = useState({ price: "", currency: "USD", lead_time_days: 30, payment_terms: "T/T 30/70", shipping_terms: "FOB", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/rfqs").then(({data}) => setRfqs(data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openRfq = async (rfq) => {
    const willOpen = open !== rfq.id;
    setOpen(willOpen ? rfq.id : null);
    // Record verifiable "viewed" event the first time the supplier expands an RFQ.
    if (willOpen) {
      try { await api.post(`/rfqs/${rfq.id}/view`); load(); } catch { /* non-blocking */ }
    }
  };

  const submit = async (e, rfq) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/quotes", {
        rfq_id: rfq.id, supplier_id: "self", price: parseFloat(form.price || 0),
        currency: form.currency, lead_time_days: parseInt(form.lead_time_days || 0),
        payment_terms: form.payment_terms, shipping_terms: form.shipping_terms,
        notes: form.notes, validity_days: 30,
      });
      toast.success("Quotation submitted");
      setOpen(null);
      setForm({ price: "", currency: "USD", lead_time_days: 30, payment_terms: "T/T 30/70", shipping_terms: "FOB", notes: "" });
      load();
    } catch (err) { toast.error(friendlyError(err)); } finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-2">Incoming RFQs</div>
        <h1 className="page-title">RFQs awaiting your quotation</h1>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_,i)=>(<div key={i} className="skeleton h-24"/>))}</div>
      ) : rfqs.length === 0 ? (
        <div className="surface p-10 text-center text-slate-500">
          <FileText className="mx-auto text-slate-300 mb-3" size={32}/>
          <div>No RFQs have been routed to your account yet.</div>
        </div>
      ) : rfqs.map((r) => (
        <div key={r.id} className="surface p-5 md:p-6" data-testid={`rfq-${r.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="section-title">{r.content?.title || "RFQ"}</div>
              <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="font-mono">{r.id.slice(0,8)}</span>
                <span className="pill bg-slate-100 text-slate-600 border-slate-200">{STATUS_LABEL[r.status] || r.status}</span>
                {r.sent_at && <span className="inline-flex items-center gap-1"><Clock size={11}/> sent {timeAgo(r.sent_at)}</span>}
              </div>
            </div>
            <button onClick={() => openRfq(r)} className="btn-gold" data-testid={`quote-btn-${r.id}`}>
              {open === r.id ? "Close" : (r.status === "quote_received" || r.status === "closed" ? "View details" : "View & quote")}
            </button>
          </div>

          {open === r.id && (
            <>
              <div className="mt-5 pt-5 border-t border-slate-200 text-sm text-slate-700 whitespace-pre-wrap">
                {r.content?.scope || r.content?.introduction}
              </div>
              {r.status !== "closed" && (
                <form onSubmit={(e) => submit(e, r)} className="mt-5 grid sm:grid-cols-2 gap-4 pt-5 border-t border-slate-200">
                  <div className="space-y-1.5"><Label>Price</Label><Input type="number" required value={form.price} onChange={(e) => setForm(f=>({...f, price:e.target.value}))} data-testid="quote-price-input"/></div>
                  <div className="space-y-1.5"><Label>Currency</Label><Input value={form.currency} onChange={(e) => setForm(f=>({...f, currency:e.target.value}))}/></div>
                  <div className="space-y-1.5"><Label>Lead time (days)</Label><Input type="number" value={form.lead_time_days} onChange={(e) => setForm(f=>({...f, lead_time_days:e.target.value}))}/></div>
                  <div className="space-y-1.5"><Label>Shipping terms</Label><Input value={form.shipping_terms} onChange={(e) => setForm(f=>({...f, shipping_terms:e.target.value}))}/></div>
                  <div className="space-y-1.5 sm:col-span-2"><Label>Payment terms</Label><Input value={form.payment_terms} onChange={(e) => setForm(f=>({...f, payment_terms:e.target.value}))}/></div>
                  <div className="space-y-1.5 sm:col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm(f=>({...f, notes:e.target.value}))}/></div>
                  <div className="sm:col-span-2 flex justify-end"><button disabled={submitting} className="btn-gold" data-testid="submit-quote-btn">{submitting ? "Submitting…" : "Submit quotation"}</button></div>
                </form>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
}
